/** Loader for the Google Maps JavaScript API.
 *
 * Pure module (no React) so it is unit-testable with a synthetic DOM, the
 * same reasoning as lib/sseClient.ts. The API key is fetched at runtime from
 * `/api/places/status` rather than baked into the bundle; it is public by
 * necessity once Maps JS runs in the page, and is expected to carry an
 * HTTP-referrer restriction (see backend core/config.py).
 *
 * The script tag is a global singleton — Google's loader throws if included
 * twice — so every caller shares one in-flight promise.
 */

const SCRIPT_ID = "google-maps-js";

// `@types/google.maps` is not a dependency — the surface we use is small and
// accessed dynamically, so it is typed as `MapsApi` rather than pulling in a
// large ambient type package.
export type MapsApi = any;

let loadPromise: Promise<MapsApi> | null = null;

export function resetMapsLoaderForTests() {
  loadPromise = null;
  document.getElementById(SCRIPT_ID)?.remove();
}

export function loadGoogleMaps(apiKey: string): Promise<MapsApi> {
  if (loadPromise) return loadPromise;

  loadPromise = new Promise((resolve, reject) => {
    if (typeof window === "undefined" || !document?.head) {
      reject(new Error("no DOM"));
      return;
    }
    // Already present (e.g. a prior mount in the same session).
    if ((window as any).google?.maps) {
      resolve((window as any).google.maps);
      return;
    }
    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.async = true;
    script.src =
      `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}` +
      `&v=weekly&loading=async`;
    script.onload = () => {
      const maps = (window as any).google?.maps;
      if (maps) resolve(maps);
      else reject(new Error("Maps JS loaded but google.maps is missing"));
    };
    script.onerror = () => {
      // Let a later mount retry (a transient network failure shouldn't
      // permanently poison the map for the rest of the session).
      loadPromise = null;
      script.remove();
      reject(new Error("Maps JS failed to load"));
    };
    document.head.appendChild(script);
  });

  return loadPromise;
}

/** Declutter rules shared by both themes. Colour is NOT set here: the map is
 * created with the API's native `colorScheme`, which also darkens Google's own
 * zoom/fullscreen controls — a custom `styles` palette recolours only the
 * tiles and leaves stark white controls floating on a dark panel. */
export const MAP_DECLUTTER_STYLES = [
  { featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] },
  { featureType: "transit", stylers: [{ visibility: "off" }] },
];
